<template>
  <div v-if="theme && design" data-test-id="giftcard-theme-page">
    <div class="mx-auto max-w-(--breakpoint-xl) px-4 py-6 md:py-8">
      <h1
        data-test-id="giftcard-theme-title"
        class="mb-6 text-h4 font-bold text-stone-800"
      >
        {{ t('personalization.theme.title', { theme: theme.name }) }}
      </h1>

      <div class="personalize-grid">
        <div class="gc-aside flex flex-col gap-4 lg:top-4">
          <div class="relative">
            <GiftcardPersonalizationPreview
              :amount="formData.amount"
              :background="{
                image: design.backgroundImage,
                color: design.backgroundColor,
              }"
              :title="message.title"
              :message="message.message"
              :buyer-name="message.buyerName"
              :recipient-name="message.recipientName"
            />
            <button
              type="button"
              class="absolute top-3 right-3 rounded-full bg-white/90 px-3 py-1.5 text-sm font-semibold text-stone-800 border border-gray-200 shadow-sm md:hidden"
              data-test-id="giftcard-preview-sheet-open"
              @click="previewOpen = true"
            >
              {{ t('personalization.theme.preview.open') }}
            </button>
          </div>
          <GiftcardPersonalizationSummary
            class="hidden md:block"
            :amount="formData.amount"
            :quantity="formData.quantity"
          />
        </div>

        <div class="gc-main flex flex-col gap-4">
          <GiftcardStepCard
            :step="1"
            :title="t('personalization.theme.steps.amount')"
            :open="openStep === 1"
            :summary="amountSummary"
            show-next
            @edit="openStep = 1"
            @next="openStep = 2"
          >
            <GiftcardAmountPicker
              ref="amountPicker"
              v-model="formData.amount"
              v-model:quantity="formData.quantity"
              :presets="amountPresets"
              :min="GIFTCARD_MIN_AMOUNT"
              :max="GIFTCARD_MAX_AMOUNT"
              :placeholder="t('personalization.theme.amount.placeholder')"
              :required-error-message="amountErrors.required"
              :min-error-message="amountErrors.min"
              :max-error-message="amountErrors.max"
              required
            >
              <template #input-label>
                <label
                  class="md:hidden mb-1 inline-flex text-label text-stone-800"
                >
                  {{ t('personalization.theme.amount.customLabel') }}
                </label>
              </template>
            </GiftcardAmountPicker>
          </GiftcardStepCard>
          <GiftcardStepCard
            :step="2"
            :title="t('personalization.theme.steps.design')"
            :open="openStep === 2"
            :summary="design.name"
            show-next
            @edit="openStep = 2"
            @next="openStep = 3"
          >
            <GiftcardDesignPicker
              v-model="formData.designId"
              :designs="designOptions"
            />
          </GiftcardStepCard>

          <GiftcardStepCard
            :step="3"
            :title="t('personalization.theme.steps.message')"
            :open="openStep === 3"
            :summary="message.title"
            @edit="openStep = 3"
          >
            <GiftcardMessageStep v-model="message" />
          </GiftcardStepCard>
        </div>
      </div>
      <GiftcardPersonalizationSummary
        class="md:hidden mt-8"
        :amount="formData.amount"
        :quantity="formData.quantity"
      />
    </div>

    <GiftcardPreviewSheet
      :open="previewOpen"
      :design-name="design.name"
      @close="previewOpen = false"
    >
      <GiftcardPersonalizationPreview
        expanded
        :amount="formData.amount"
        :background="{
          image: design.backgroundImage,
          color: design.backgroundColor,
        }"
        :title="message.title"
        :message="message.message"
        :buyer-name="message.buyerName"
        :recipient-name="message.recipientName"
      />
      <template #designs>
        <GiftcardDesignPicker
          v-model="formData.designId"
          :designs="designOptions"
          row
        />
      </template>
    </GiftcardPreviewSheet>

    <!-- same sticky bar as /giftcard/personalize -->
    <div class="sticky bottom-0 z-10 border-t border-gray-200 bg-white">
      <div class="mx-auto max-w-(--breakpoint-xl) px-4 pt-2 pb-4 md:py-3">
        <Button
          class="w-full lg:w-80 lg:py-4"
          data-test-id="giftcard-personalization-cta"
          @click="handleSubmit"
        >
          {{ ctaLabel }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import GiftcardAmountPicker from '~/components/giftcard/GiftcardAmountPicker.vue'
import GiftcardDesignPicker from '~/components/giftcard/GiftcardDesignPicker.vue'
import GiftcardMessageStep from '~/components/giftcard/GiftcardMessageStep.vue'
import GiftcardPersonalizationPreview from '~/components/giftcard/GiftcardPersonalizationPreview.vue'
import GiftcardPersonalizationSummary from '~/components/giftcard/GiftcardPersonalizationSummary.vue'
import GiftcardPreviewSheet from '~/components/giftcard/GiftcardPreviewSheet.vue'
import GiftcardStepCard from '~/components/giftcard/GiftcardStepCard.vue'
import type { GiftcardMessage } from '~/components/giftcard/GiftcardMessageStep.vue'
import type { GiftcardCartItem } from '~/composables/types/cart'
import {
  GIFTCARD_MAX_AMOUNT,
  GIFTCARD_MIN_AMOUNT,
} from '~/utils/giftcardAmount'

definePageMeta({
  layout: 'minimal',
  contentGroup: 'giftcard',
  pageBackground: '#f8f6f2',
  hideFooter: true,
})

interface ThemedGiftcardFormData {
  designId: string
  amount: number | null
  quantity: number
  /** unset fields fall back to the selected design's sample title/message */
  message: GiftcardMessage
}

const { t, locale } = useI18n()
const route = useRoute()
const { cart, addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState('general-instance')
const { pricingsFactory } = usePricingsFactory()
const $localePath = useLocalePath()

const subdomain = useSubdomain()
const { data: partnerData } = subdomain.value
  ? await useSubdomainData()
  : { data: ref(undefined) }

const supplierId = computed(() => supplierGetters.getId(partnerData.value))
const supplierName = computed(
  () => supplierGetters.getName(partnerData.value) || 'konfetti'
)

const { data: themes } = await useGiftcardThemes()

const theme = computed(() =>
  themes.value?.find((item) => item.slug === route.params.theme)
)

if (!theme.value?.designs.length) {
  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const { data: formData } = useKftLocalStorage<ThemedGiftcardFormData>(
  `giftcard-theme-form-data-${theme.value.id}`,
  {
    designId: theme.value.designs[0]!.id,
    amount: 50,
    quantity: 1,
    message: {},
  }
)

// a restored draft can point at a design that was since removed in the
// back office - fall back to the theme's first design
const design = computed(
  () =>
    theme.value?.designs.find((item) => item.id === formData.value.designId) ??
    theme.value?.designs[0]
)

const designOptions = computed(() =>
  theme.value!.designs.map((item) => ({
    id: item.id,
    label: item.name,
    image: item.swatchImage,
    color: item.backgroundColor,
  }))
)

const message = computed<GiftcardMessage>({
  get: () => ({
    ...formData.value.message,
    title: formData.value.message.title ?? design.value?.sampleTitle,
    message: formData.value.message.message ?? design.value?.sampleMessage,
  }),
  set: (value) => {
    formData.value.message = value
  },
})

const amountPresets = [
  parseInt(t('giftcard.option_1')),
  parseInt(t('giftcard.option_2')),
  parseInt(t('giftcard.option_3')),
  parseInt(t('giftcard.option_4')),
]

const formatEuro = (value: number) => formatMoney(value, locale.value, 'EUR', 0)

const amountErrors = computed(() => ({
  required: t('personalization.theme.amount.required', {
    min: formatEuro(GIFTCARD_MIN_AMOUNT),
    max: formatEuro(GIFTCARD_MAX_AMOUNT),
  }),
  min: t('personalization.theme.amount.min', {
    min: formatEuro(GIFTCARD_MIN_AMOUNT),
  }),
  max: t('personalization.theme.amount.max', {
    max: formatEuro(GIFTCARD_MAX_AMOUNT),
  }),
}))

const amountSummary = computed(() =>
  formData.value.amount === null
    ? undefined
    : `${formData.value.quantity} × ${formatMoney(formData.value.amount, locale.value, 'EUR', 2)}`
)

// one step open at a time
const openStep = ref(1)
const previewOpen = ref(false)

const amountPicker = ref<{ checkError: () => boolean } | null>(null)
const { scrollToFirstFormError } = useScrollToFirstFormError()

const ctaLabel = computed(() =>
  cartGetters.getTotalItems(cart.value) > 0
    ? t('personalization.cta.goToCart')
    : t('personalization.cta.goToCheckout')
)

const handleSubmit = async () => {
  const isAmountValid = amountPicker.value?.checkError() ?? true
  const amount = formData.value.amount
  if (!isAmountValid || amount === null) {
    openStep.value = 1
    await scrollToFirstFormError()
    return
  }

  const hadExistingItems = cartGetters.getTotalItems(cart.value) > 0

  const cartItem: GiftcardCartItem = {
    ...parseGiftcardWithoutEvent(
      pricingsFactory(amount * 100, {
        totalParticipants: formData.value.quantity,
      }),
      t('cart.giftcardTitle'),
      supplierId.value,
      // same id as /giftcard/personalize: the cart holds one personalized
      // giftcard line, so a themed card replaces it rather than adding a second
      'giftcard_personalize',
      supplierName.value
    ),
    personalizationDelivery: {
      mode: 'SELF',
      title: message.value.title || undefined,
      message: message.value.message || undefined,
      buyerName: message.value.buyerName || undefined,
      recipientName: message.value.recipientName || undefined,
    },
    giftcardDesignId: design.value!.id,
    quantity: formData.value.quantity,
  }

  await addItem(cartItem)

  if (hadExistingItems) {
    toggleCartSidebar()
  } else {
    await navigateTo($localePath('/checkout'))
  }
}
</script>

<style lang="css" scoped>
/* same two-column layout as /giftcard/personalize */
.personalize-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-areas:
    'aside'
    'main';
}

.gc-aside {
  grid-area: aside;
}
.gc-main {
  grid-area: main;
}

@media (max-width: 399px) {
  .gc-aside {
    min-width: 0;
  }
}

@media (min-width: 1024px) {
  .personalize-grid {
    grid-template-columns: 1fr 462px;
    align-items: start;
    gap: 2rem;
    grid-template-areas: 'main aside';
  }
}
</style>
